/** biome-ignore-all lint/a11y/useSemanticElements: a real <button> cannot contain the block content this row carries */
import { useTranslation } from 'react-i18next';
import type { Loop } from '@/common/api/types/loops';
import type { ObjectiveHealth } from '@/common/api/types/objectives';
import type { PersonaView } from '@/features/agentic-insights/components/data/resolvePersonaView';
import { getPrimaryRecommendation } from '@/features/agentic-insights/components/data/resolvePersonaView';
import type { Finding } from '@/features/agentic-insights/components/data/types';
import { RunProse } from '@/features/agentic-insights/components/experimental/shared/prose/RunProse';
import { MetaLine } from '@/features/agentic-insights/components/experimental/shared/ui/controls';
import { Field } from '@/features/agentic-insights/components/experimental/shared/ui/Field';
import {
  CONFIDENCE_TONE,
  GUTTER,
  MEASURE,
  RECOMMENDATION_SURFACE,
  ROW_INTERACTIVE,
  TYPE,
} from '../language';
import { useFindingMeta } from './findingMeta';
import { LoopStamp } from './LoopStamp';

interface FindingRowProps {
  finding: Finding;
  view: Extract<PersonaView, { kind: 'voiced' }>;
  ordinal: number;
  /** Keyed by objective id, for the meta line's objective-scope entry. Undefined while loading. */
  objectiveHealthById: Record<number, ObjectiveHealth> | undefined;
  isObjectiveHealthPending: boolean;
  isOpen: boolean;
  /** First in its group, where the group heading's own rule already separates it. */
  isFirstInGroup: boolean;
  /** Present only in the All loops reading — see `LoopStamp`. */
  loop?: Loop;
  onSwitchLoop?: (loopId: number) => void;
  onOpen: () => void;
}

/**
 * One numbered entry: the number in a fixed gutter, the claim set large, and the recommendation
 * under it as a block of its own.
 *
 * The recommendation is stated on the row and not only inside the expansion. A reader with a few
 * minutes before a review who sees no action at the surface does not go looking for one, so the
 * claim being the only thing on a collapsed row cost the surface the very thing it is measured on.
 * It is a labelled block rather than a second headline: the claim stays the largest thing here,
 * because leading with the action would make the run a task list that happens to explain itself.
 *
 * A recommendation that says there is nothing to do is not shown as an action: rendered as one it
 * reads as a call to act, which is the manufactured urgency the "nothing to do" slot exists to
 * prevent.
 */
export const FindingRow = ({
  finding,
  view,
  ordinal,
  objectiveHealthById,
  isObjectiveHealthPending,
  isOpen,
  isFirstInGroup,
  loop,
  onSwitchLoop,
  onOpen,
}: FindingRowProps) => {
  const { t } = useTranslation();
  const baseMeta = useFindingMeta(
    finding,
    CONFIDENCE_TONE,
    objectiveHealthById,
    isObjectiveHealthPending,
  );
  // Confidence first, then the loop (All loops only), then whatever else the finding carries —
  // matches the ticket's example ordering: `HIGH CONFIDENCE · MY LOOP · WHOLE LOOP`.
  const meta =
    loop && onSwitchLoop
      ? [
          baseMeta[0],
          <LoopStamp key="loop" loop={loop} onSwitch={onSwitchLoop} />,
          ...baseMeta.slice(1),
        ]
      : baseMeta;

  const next = getPrimaryRecommendation(view.recommendations);
  const nextAction = next?.noActionNeeded ? undefined : next;

  return (
    <div
      // A div with `role="button"` rather than a real one so the row stays one target while
      // carrying block-level content, with explicit key handling so it remains reachable.
      role="button"
      tabIndex={0}
      data-testid="agentic-x-finding"
      aria-expanded={isOpen}
      onClick={onOpen}
      onKeyDown={(event) => {
        if (event.key !== 'Enter' && event.key !== ' ') return;
        event.preventDefault();
        onOpen();
      }}
      // An open row sticks to the top of the scroll area while its own expansion is on screen, so a
      // reader deep in the evidence can still see which claim they are reading about. The sticky
      // context is the wrapper holding this row and its panel, so it releases at the next finding
      // rather than stacking up.
      // Two things fight over an open row's background, and both have to be satisfied at once.
      //
      // It is sticky, so the background must be OPAQUE — every `-subtle`/`-subtler` token is a
      // `color-mix` with transparent (brand-subtler is 8% ink), and the expansion's text scrolls
      // visibly through one. And `ROW_INTERACTIVE` sets a hover background-COLOR, which replaces
      // whatever opaque colour the row was painted with the moment the pointer lands on it.
      //
      // So the tint is a background-IMAGE — a flat gradient, immune to the hover colour swap — laid
      // over opaque colours for both rest and hover. Written as a token-backed arbitrary value
      // (commas, no spaces) for the same reason `findingDisplay` writes its washes that way: the
      // semantic-token lint accepts that form.
      className={`group/finding flex items-start gap-3 py-5 ${isFirstInGroup ? '' : 'border-t border-border-neutral-subtle'} ${ROW_INTERACTIVE} ${
        isOpen
          ? 'sticky top-0 z-10 bg-[linear-gradient(var(--color-bg-brand-subtler),var(--color-bg-brand-subtler))] bg-bg-surface-foundation hover:bg-bg-surface-base'
          : ''
      }`}
    >
      <span className={`${GUTTER} ${TYPE.ordinal} pt-1 text-right`}>
        <Field name="ordinal">{ordinal}</Field>
      </span>
      <span className="flex min-w-0 flex-col gap-2">
        {/* Clamped here and nowhere else. Nothing upstream enforces a short claim — runs stored
            under an earlier prompt carry whole paragraphs — and at this size an unclamped paragraph
            costs the list the even rhythm that makes a numbered argument scannable. The full text is
            one click away in the argument. */}
        <span className={`${TYPE.claim} ${MEASURE} ${isOpen ? '' : 'line-clamp-3'}`}>
          <Field name="businessStatement">
            <RunProse>{view.businessStatement}</RunProse>
          </Field>
        </span>

        {/* Directly under the claim, because every value on it — confidence, scope, the metric —
            describes the finding. Sat under the action instead, it read as the action's standing,
            and confidence in particular is a property of the claim and not of what to do about it. */}
        <MetaLine type={TYPE} items={meta} testId="agentic-x-finding-meta" />

        {nextAction && !isOpen ? (
          // Bounded, so it reads as the second assertion the row is making rather than as a
          // footnote to the claim — but drawn exactly as the same recommendation is drawn inside the
          // expansion: the arrow in its own marker column, the title on one left edge beside it. A
          // reader who opens the finding should meet the line they just read, not a restyled version
          // of it. The title alone here; its detail, steps and parameters are what opening is for.
          <span
            data-testid="agentic-x-next"
            className={`${RECOMMENDATION_SURFACE} ${MEASURE} grid grid-cols-[1.25rem_1fr] gap-x-2 px-3 py-2`}
          >
            <span aria-hidden="true" className={`${TYPE.action} text-text-brand-primary`}>
              →
            </span>
            <span className={`${TYPE.action} min-w-0 text-base`}>
              <Field name="recommendations[primary].businessTitle">
                <RunProse>{nextAction.businessTitle}</RunProse>
              </Field>
            </span>
          </span>
        ) : nextAction || isOpen ? null : (
          <span className={`${TYPE.meta} ${CONFIDENCE_TONE.High}`}>
            {t(($) => $.loopAgenticInsights.recommendations.noActionNeeded)}
          </span>
        )}
      </span>
    </div>
  );
};
